import {
  GOOGLE_INTEGRATIONS_TITLE,
  OUTLOOK_INTEGRATIONS_TITLE,
  PROVIDER_PERMISSIONS,
  ZENDESK_INTEGRATIONS_TITLE,
  ZOOM_TITLE,
} from "@tellescope/constants";
import {
  DEFAULT_ADMIN_ROLE as BASE_ADMIN_ROLE,
  DEFAULT_NON_ADMIN_ROLE as BASE_NON_ADMIN_ROLE,
  OrgPlanName,
  TellescopeModelFromName,
} from "@tellescope/tanstack-query";
import type { FeatureName } from "../../../../permissions";

type RoleModel = TellescopeModelFromName<"role_based_access_permissions">;

export type RolePermissions = RoleModel["permissions"];
export type AccessResource = keyof RolePermissions;
export type ResourceAccess = NonNullable<RolePermissions[AccessResource]>;
export type AccessLevel = ResourceAccess["read"];
export type RoleUIRestrictions = NonNullable<RoleModel["uiRestrictions"]>;
export type RoleFieldRedactions = NonNullable<RoleModel["fieldRedactions"]>;
export type RolePortalSchemaRestrictions = NonNullable<RoleModel["portalSchemaRestrictions"]>;
export type PortalSchemaRestrictionKey = keyof RolePortalSchemaRestrictions;

export interface RoleDraft {
  role: string;
  color?: string;
  description?: string;
  permissions: RolePermissions;
  uiRestrictions: RoleUIRestrictions;
  fieldRedactions: RoleFieldRedactions;
  portalSchemaRestrictions: RolePortalSchemaRestrictions;
}

export const PORTAL_SCHEMA_RESTRICTION_KEYS: PortalSchemaRestrictionKey[] = [
  "disableEditContent",
  "disableEditTheming",
  "disableEditSnippets",
];

export const ACCESS_ACTIONS = ["create", "read", "update", "delete"] as const;
export type AccessAction = (typeof ACCESS_ACTIONS)[number];

/** Sentinel used by the access-level <Select>, since Radix selects can't hold a null/empty value. */
const NO_ACCESS_VALUE = "none";
export type AccessSelectValue = "All" | "Assigned" | "Default" | typeof NO_ACCESS_VALUE;

export const ACCESS_SELECT_VALUES: AccessSelectValue[] = ["All", "Assigned", "Default", NO_ACCESS_VALUE];

/** Per-value color treatment for the access-level trigger, so a role's access is scannable at a glance. */
export const ACCESS_SELECT_COLORS: Record<AccessSelectValue, string> = {
  All: "bg-green-50 text-green-800 [&_svg:not([class*='text-'])]:text-green-800",
  Assigned: "bg-blue-50 text-blue-800 [&_svg:not([class*='text-'])]:text-blue-800",
  Default: "bg-gray-50 text-gray-700 [&_svg:not([class*='text-'])]:text-gray-700",
  [NO_ACCESS_VALUE]: "bg-red-50 text-red-800 [&_svg:not([class*='text-'])]:text-red-800",
};

export const parseAccessLevel = (value: string): AccessLevel =>
  value === "All" || value === "Assigned" || value === "Default" ? value : null;
export const toAccessSelectValue = (level: AccessLevel | undefined): AccessSelectValue => level ?? NO_ACCESS_VALUE;

export const emptyResourceAccess = (): ResourceAccess => ({ create: null, read: null, update: null, delete: null });

/**
 * The effective access for a resource when a role doesn't list it. Unspecified resources inherit the
 * organization's `PROVIDER_PERMISSIONS` baseline at auth time (a shallow, resource-level merge), so the
 * editor seeds and displays from this baseline rather than treating an absent resource as "No Access".
 * Resources with no provider baseline are inaccessible by default (all actions denied).
 */
export const providerResourceAccess = (resource: AccessResource): ResourceAccess =>
  PROVIDER_PERMISSIONS[resource] ?? emptyResourceAccess();

/**
 * A built-in role definition shaped like a {@link RoleModel} record but never persisted — it has no id
 * and isn't returned by `getSome`. These are the implicit roles every org has. The role string +
 * permissions are the domain primitives from `@tellescope/tanstack-query`; `color`/`description` are the
 * app-local UI presentation layered on top.
 */
export interface DefaultRoleDefinition {
  role: string;
  permissions: RolePermissions;
  color?: string;
  description?: string;
}

/** The implicit "Admin" role ({@link BASE_ADMIN_ROLE}) with its app-local card color. */
export const DEFAULT_ADMIN_ROLE: DefaultRoleDefinition = { ...BASE_ADMIN_ROLE, color: "#7c3aed" };

/** The implicit non-admin baseline ({@link BASE_NON_ADMIN_ROLE}) with its app-local card color. */
export const DEFAULT_NON_ADMIN_ROLE: DefaultRoleDefinition = { ...BASE_NON_ADMIN_ROLE, color: "#64748b" };

export interface DataAccessFeature {
  /** Stable id; also the key used to look up the display label. */
  key: string;
  /** Resources the feature groups — the chosen access level applies to all of them. */
  resources: AccessResource[];
  /** When true, the Show in Sidebar checkbox is locked. */
  sidebarLocked: boolean;
  /** Organization is special: only the Update column is editable. */
  updateOnly?: boolean;
  /** Plan feature this row maps to — used to apply per-plan display-name overrides (e.g. "Appointment Templates"). */
  feature?: FeatureName;
}

export const DATA_ACCESS_FEATURES: DataAccessFeature[] = [
  {
    key: "contacts",
    resources: ["endusers", "enduser_tasks", "enduser_status_updates"],
    sidebarLocked: false,
    feature: "contacts",
  },
  { key: "contactViews", resources: ["enduser_views"], sidebarLocked: true, feature: "contacts" },
  { key: "users", resources: ["users"], sidebarLocked: true },
  { key: "organization", resources: ["organizations"], sidebarLocked: true, updateOnly: true },
  { key: "tickets", resources: ["tickets"], sidebarLocked: false, feature: "tickets" },
  { key: "calendarEvents", resources: ["calendar_events"], sidebarLocked: false, feature: "calendar" },
  { key: "calendarEventLocations", resources: ["appointment_locations"], sidebarLocked: true, feature: "calendar" },
  {
    key: "calendarEventTemplates",
    resources: ["calendar_event_templates"],
    sidebarLocked: true,
    feature: "calendar",
  },
  { key: "calendarBookingPages", resources: ["appointment_booking_pages"], sidebarLocked: true, feature: "calendar" },
  { key: "databases", resources: ["databases"], sidebarLocked: false },
  { key: "databaseEntries", resources: ["database_records"], sidebarLocked: true },
  { key: "forms", resources: ["forms"], sidebarLocked: false },
  { key: "formQuestions", resources: ["form_fields"], sidebarLocked: true },
  { key: "formResponses", resources: ["form_responses"], sidebarLocked: true },
  { key: "files", resources: ["files"], sidebarLocked: false },
  { key: "faxes", resources: ["fax_logs"], sidebarLocked: true },
  { key: "journeys", resources: ["journeys", "automation_steps"], sidebarLocked: false },
  { key: "triggers", resources: ["automation_triggers"], sidebarLocked: true },
  { key: "templates", resources: ["templates"], sidebarLocked: false, feature: "templates" },
  {
    key: "community",
    resources: ["forums", "forum_posts", "post_comments", "post_likes", "comment_likes"],
    sidebarLocked: false,
  },
  { key: "content", resources: ["managed_content_records"], sidebarLocked: false },
  { key: "contentAssignment", resources: ["managed_content_record_assignments"], sidebarLocked: true },
  { key: "integrations", resources: ["integrations"], sidebarLocked: false },
  { key: "backgroundErrors", resources: ["background_errors"], sidebarLocked: true },
  { key: "smsMessages", resources: ["sms_messages"], sidebarLocked: true, feature: "messaging" },
  { key: "emails", resources: ["emails"], sidebarLocked: true, feature: "messaging" },
  { key: "phoneCalls", resources: ["phone_calls"], sidebarLocked: true, feature: "call" },
  { key: "purchases", resources: ["purchases"], sidebarLocked: true },
  { key: "chatRooms", resources: ["chat_rooms", "chats"], sidebarLocked: true, feature: "team-chat" },
  { key: "analytics", resources: ["analytics_frames"], sidebarLocked: false },
  { key: "engagementEvents", resources: ["engagement_events"], sidebarLocked: true },
  { key: "notes", resources: ["notes"], sidebarLocked: true },
  { key: "medications", resources: ["enduser_medications"], sidebarLocked: true },
  { key: "vitalsConfigurations", resources: ["vital_configurations"], sidebarLocked: true, feature: "vitals" },
  { key: "portalCustomizations", resources: ["portal_customizations"], sidebarLocked: false },
  { key: "phoneTrees", resources: ["phone_trees"], sidebarLocked: true, feature: "call" },
  { key: "tableViews", resources: ["table_views"], sidebarLocked: true, feature: "table_views" },
  { key: "customDashboards", resources: ["custom_dashboards"], sidebarLocked: true, feature: "dashboard" },
  { key: "apiKeys", resources: ["apiKeys"], sidebarLocked: true },
  { key: "timeTracks", resources: ["time_tracks"], sidebarLocked: true },
];

export type BooleanUIRestrictionKey =
  | "hideDashboard"
  | "hideInbox"
  | "hideTeamChat"
  | "disableTicketDueDate"
  | "disableUnstructuredNotes"
  | "hideEnduserChat"
  | "hideEnduserNote"
  | "hideCareplan"
  | "hideUnsubmittedForms"
  | "hideMergeEndusers"
  | "hideQueuedTicketsViewer"
  | "hideIncomingFaxesIcon"
  | "hideNotificationsIcon"
  | "hideBulkEnduserActions"
  | "hideViewPortalAsEnduser"
  | "hideCalendarUserSelector"
  | "hideCalendarSavedViews"
  | "hideCalendarFilters"
  | "disableTimeTrackApproval";

/**
 * UI restriction toggles. `feature` (optional) gates the toggle by the org's plan feature set — when
 * set and the feature isn't enabled, the toggle is hidden. Toggles without a feature are always shown.
 * The mapping is intentionally ad-hoc: each toggle is tied to the page/area it affects.
 *
 * Items that are commented out are not yet implemented in T2
 */
export const UI_RESTRICTION_TOGGLES: { key: BooleanUIRestrictionKey; feature?: FeatureName }[] = [
  { key: "hideDashboard", feature: "dashboard" },
  { key: "hideInbox", feature: "messaging" },
  { key: "hideTeamChat", feature: "team-chat" },
  { key: "disableTicketDueDate", feature: "tickets" },
  { key: "disableUnstructuredNotes", feature: "contacts" },
  { key: "hideEnduserChat", feature: "contacts" },
  { key: "hideEnduserNote", feature: "contacts" },
  // { key: "hideCareplan", feature: "contacts" },
  { key: "hideUnsubmittedForms", feature: "contacts" },
  // { key: "hideMergeEndusers", feature: "contacts" },
  // { key: "hideQueuedTicketsViewer", feature: "tickets" },
  // { key: "hideIncomingFaxesIcon" },
  // { key: "hideNotificationsIcon" },
  // { key: "hideBulkEnduserActions", feature: "contacts" },
  // { key: "hideViewPortalAsEnduser", feature: "contacts" },
  { key: "hideCalendarUserSelector", feature: "calendar" },
  { key: "hideCalendarSavedViews", feature: "calendar" },
  { key: "hideCalendarFilters", feature: "calendar" },
  // { key: "disableTimeTrackApproval" },
];

export const VISIBLE_INTEGRATION_VALUES: string[] = [
  "Google Calendar",
  GOOGLE_INTEGRATIONS_TITLE,
  OUTLOOK_INTEGRATIONS_TITLE,
  ZENDESK_INTEGRATIONS_TITLE,
  ZOOM_TITLE,
];

export type FieldRestrictionList = "hiddenFields" | "disabledFields";

export const PHONE_CALL_REDACTION_MODEL = "phone_calls";

export interface PhoneCallRedactionGroup {
  key: string;
  fields: string[];
}

export const PHONE_CALL_REDACTION_GROUPS: PhoneCallRedactionGroup[] = [
  { key: "recordings", fields: ["recordingURI", "recordingId", "recordingDurationInSeconds"] },
  { key: "voicemailTranscription", fields: ["transcription"] },
  { key: "callTranscription", fields: ["recordingTranscriptionData"] },
  { key: "callSummary", fields: ["aiSummary"] },
];

export const getFeatureAccess = (permissions: RolePermissions, feature: DataAccessFeature, action: AccessAction) => {
  const primary = feature.resources[0];
  if (!primary) return null;
  // An unspecified resource inherits the provider baseline; only an explicitly-stored object overrides it.
  const stored = permissions[primary];
  return (stored ?? providerResourceAccess(primary))[action] ?? null;
};

export const getFeatureSidebar = (permissions: RolePermissions, feature: DataAccessFeature) => {
  const primary = feature.resources[0];
  // The router shows a nav entry unless showInSidebar is explicitly false, so absent/inherited defaults to shown.
  return !primary || permissions[primary]?.showInSidebar !== false;
};

export const isRedactionGroupChecked = (fieldRedactions: RoleFieldRedactions, fields: string[]) => {
  const redacted = fieldRedactions[PHONE_CALL_REDACTION_MODEL] ?? [];
  return fields.every((field) => redacted.includes(field));
};

/**
 * Per-plan allowlist of Data Access feature keys (see {@link DATA_ACCESS_FEATURES}). A plan only
 * shows the rows whose key is listed. There is no granular per-resource feature system in the org
 * feature layer, so this app-local map is the source of truth for which features each plan exposes.
 */
export const PLAN_DATA_ACCESS_FEATURES: Record<OrgPlanName, ReadonlySet<string>> = {
  "Healthie-Text": new Set([
    "contacts",
    "users",
    "organization",
    "templates",
    "integrations",
    "smsMessages",
    "emails",
    "phoneCalls",
    "chatRooms",
  ]),
};

/** Whether a Data Access feature row should be shown for the org's plan. Unknown plans grant everything. */
export const isFeatureAllowedForPlan = (plan: OrgPlanName | undefined, featureKey: string) =>
  !plan || PLAN_DATA_ACCESS_FEATURES[plan].has(featureKey);
